"use client";

import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import { AppStoreCTA } from "@/app/components/AppStoreCTA";

const MOBILE_LINKS = [
  { href: "/#how-it-works", label: "How it works" },
  { href: "/#features", label: "Features" },
  { href: "/#pricing", label: "Pricing" },
  { href: "/for", label: "Use Cases" },
  { href: "/compare", label: "Comparisons" },
  { href: "/learn", label: "Learn" },
];

export function Nav() {
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  function openMenu() {
    if (closeTimer.current !== null) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setResourcesOpen(true);
  }

  function scheduleClose() {
    closeTimer.current = setTimeout(() => {
      setResourcesOpen(false);
      closeTimer.current = null;
    }, 180);
  }

  return (
    <nav
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-sm border-b"
      style={{ backgroundColor: "#EDEADE", borderColor: "rgba(90,122,56,0.15)" }}
    >
      <div className="max-w-6xl mx-auto px-8 h-16 flex items-center justify-between">
        {/* Wordmark */}
        <Link
          href="/"
          className="font-fraunces font-bold text-3xl tracking-tight"
          style={{ color: "#3D5225" }}
        >
          mendly
        </Link>

        {/* Desktop nav links */}
        <div className="hidden md:flex items-center gap-10">
          <a
            href="/#how-it-works"
            className="font-nunito font-semibold text-base transition-colors"
            style={{ color: "#1C1917" }}
            onMouseOver={(e) => (e.currentTarget.style.color = "#5A7A38")}
            onMouseOut={(e) => (e.currentTarget.style.color = "#1C1917")}
          >
            How it works
          </a>
          <a
            href="/#features"
            className="font-nunito font-semibold text-base transition-colors"
            style={{ color: "#1C1917" }}
            onMouseOver={(e) => (e.currentTarget.style.color = "#5A7A38")}
            onMouseOut={(e) => (e.currentTarget.style.color = "#1C1917")}
          >
            Features
          </a>
          <a
            href="/#pricing"
            className="font-nunito font-semibold text-base transition-colors"
            style={{ color: "#1C1917" }}
            onMouseOver={(e) => (e.currentTarget.style.color = "#5A7A38")}
            onMouseOut={(e) => (e.currentTarget.style.color = "#1C1917")}
          >
            Pricing
          </a>

          {/* Resources dropdown
              The wrapper div owns both the trigger and the panel.
              onMouseEnter/Leave live on the wrapper — no dead-space gap
              between button and panel. A transparent pt-2 bridge inside the
              absolute container keeps the hover zone continuous. */}
          <div
            className="relative"
            onMouseEnter={openMenu}
            onMouseLeave={scheduleClose}
          >
            <button
              onClick={() => {
                if (closeTimer.current !== null) {
                  clearTimeout(closeTimer.current);
                  closeTimer.current = null;
                }
                setResourcesOpen((o) => !o);
              }}
              className="font-nunito font-semibold text-base transition-colors flex items-center gap-1"
              style={{
                color: resourcesOpen ? "#5A7A38" : "#1C1917",
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
              }}
            >
              Resources
              <svg
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none"
                style={{
                  marginTop: "1px",
                  transition: "transform 0.15s",
                  transform: resourcesOpen ? "rotate(180deg)" : "rotate(0deg)",
                }}
              >
                <path
                  d="M2 4l4 4 4-4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>

            {resourcesOpen && (
              // pt-2 is a transparent hover bridge — keeps the zone
              // unbroken from button bottom to panel top
              <div
                className="absolute top-full left-0 pt-2 z-50"
                style={{ minWidth: "160px" }}
              >
                <div
                  className="rounded-xl border py-1"
                  style={{
                    backgroundColor: "#EDEADE",
                    borderColor: "rgba(90,122,56,0.2)",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                  }}
                >
                  <Link
                    href="/for"
                    className="block px-4 py-2.5 font-nunito font-semibold text-sm transition-colors hover:text-[#5A7A38]"
                    style={{ color: "#1C1917" }}
                  >
                    Use Cases
                  </Link>
                  <Link
                    href="/compare"
                    className="block px-4 py-2.5 font-nunito font-semibold text-sm transition-colors hover:text-[#5A7A38]"
                    style={{ color: "#1C1917" }}
                  >
                    Comparisons
                  </Link>
                  <Link
                    href="/learn"
                    className="block px-4 py-2.5 font-nunito font-semibold text-sm transition-colors hover:text-[#5A7A38]"
                    style={{ color: "#1C1917" }}
                  >
                    Learn
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Desktop CTA — hidden below md, hamburger takes its place */}
        <div className="hidden md:block">
          <AppStoreCTA size="small" />
        </div>

        {/* Mobile hamburger / close toggle */}
        <button
          onClick={() => setMobileOpen((o) => !o)}
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
          className="md:hidden flex items-center justify-center"
          style={{
            width: "40px",
            height: "40px",
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "#1C1917",
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            {mobileOpen ? (
              <path
                d="M6 6l12 12M18 6L6 18"
                stroke="currentColor"
                strokeWidth="1.75"
                strokeLinecap="round"
              />
            ) : (
              <>
                <path d="M4 7h16" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
                <path d="M4 12h16" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
                <path d="M4 17h16" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
              </>
            )}
          </svg>
        </button>
      </div>

      {/* Mobile menu overlay + slide-in panel — portaled to document.body
          rather than rendered inline here. `backdrop-blur-sm` above on
          <nav> applies a backdrop-filter, which per spec establishes a new
          containing block for `position: fixed` descendants (the same way
          `transform` does). Left inline, this panel's `top`/`bottom`
          offsets were resolving against nav's own ~65px box instead of the
          viewport, collapsing it to ~0px tall — the backdrop rendered
          invisibly and the menu links overflowed on top of the page
          content instead of over an opaque, correctly-sized panel. */}
      {mobileOpen &&
        mounted &&
        createPortal(
          <div className="md:hidden">
            <div
              className="fixed inset-0"
              style={{ top: "64px", backgroundColor: "rgba(28,25,23,0.35)", zIndex: 40 }}
              onClick={() => setMobileOpen(false)}
            />
            <div
              className="fixed top-16 right-0 bottom-0 flex flex-col"
              style={{
                width: "78vw",
                maxWidth: "320px",
                backgroundColor: "#EDEADE",
                borderLeft: "1px solid rgba(90,122,56,0.15)",
                zIndex: 41,
                boxShadow: "-8px 0 24px rgba(0,0,0,0.08)",
              }}
            >
              <div className="flex flex-col px-8 py-8 gap-1">
                {MOBILE_LINKS.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    onClick={() => setMobileOpen(false)}
                    className="font-nunito font-semibold text-lg py-3 transition-colors"
                    style={{ color: "#1C1917" }}
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
              <div className="mt-auto px-8 py-8">
                <AppStoreCTA size="medium" />
              </div>
            </div>
          </div>,
          document.body
        )}
    </nav>
  );
}
